/**
 * publish-helpers.ts
 *
 * Extracts shareable content (Skills, Agents, Tools, Workflow graph, Team)
 * from demo scenario data, and generates recommendation-style copy for each ability type.
 */

import type { AgentDemoStep } from "@/lib/agent-demo-data"
import type { AbilityType } from "./types"
import {
  SCENARIO_SKILLS_MAP,
  type ScenarioSkill,
} from "@/lib/demo-scenarios/scenario-skills"

// ── Shareable item types ──

export interface ShareableSkill {
  id: string // name as id
  name: string
  description: string
  role: string
  privacy: string
  privacyLabel: string
  source: string
  installed?: boolean
}

export interface ShareableAgent {
  id: string
  name: string
  role: string
  description: string
  skillNames: string[]
  toolCount: number
}

export interface ShareableTool {
  id: string // "agentId:tool"
  tool: string
  label: string
  category: string
  result?: string
  agentName: string
  agentId: string
}

export interface WorkflowNode {
  id: string
  name: string
  role: string
  dependencies: string[]
}

// ── Extraction functions ──

export function extractSkills(scenarioId: string | undefined): ShareableSkill[] {
  if (!scenarioId) return []
  const meta = SCENARIO_SKILLS_MAP[scenarioId]
  if (!meta) return []
  return meta.skills.map((s: ScenarioSkill) => ({
    id: s.name,
    name: s.name,
    description: s.description,
    role: s.role,
    privacy: s.privacy,
    privacyLabel: s.privacyLabel,
    source: s.source,
    installed: s.installed,
  }))
}

export function extractAgents(demoSteps: AgentDemoStep[]): ShareableAgent[] {
  const subagentsStep = demoSteps.find((s) => s.type === "subagents")
  if (!subagentsStep?.subAgents) return []
  return subagentsStep.subAgents.map((a) => ({
    id: a.id,
    name: a.name,
    role: a.role,
    description: a.description,
    skillNames: a.skills.map((s) => s.name),
    toolCount: a.skills.reduce((sum, s) => sum + s.toolCalls.length, 0),
  }))
}

export function extractTools(demoSteps: AgentDemoStep[]): ShareableTool[] {
  const subagentsStep = demoSteps.find((s) => s.type === "subagents")
  if (!subagentsStep?.subAgents) return []
  const tools: ShareableTool[] = []
  for (const agent of subagentsStep.subAgents) {
    for (const skill of agent.skills) {
      for (const tc of skill.toolCalls) {
        tools.push({
          id: `${agent.id}:${tc.tool}`,
          tool: tc.tool,
          label: tc.label,
          category: tc.category,
          result: tc.result,
          agentName: agent.name,
          agentId: agent.id,
        })
      }
    }
  }
  return tools
}

export function extractWorkflowGraph(demoSteps: AgentDemoStep[]): WorkflowNode[] {
  const subagentsStep = demoSteps.find((s) => s.type === "subagents")
  if (!subagentsStep?.subAgents) return []
  return subagentsStep.subAgents.map((a) => ({
    id: a.id,
    name: a.name,
    role: a.role,
    dependencies: a.dependencies ?? [],
  }))
}

// ── Scenario keyword → domain mapping ──

function scenarioDomain(scenarioTitle: string): string {
  if (scenarioTitle.includes("自媒体")) return "内容创作"
  if (scenarioTitle.includes("飞书")) return "协同办公"
  if (scenarioTitle.includes("电商")) return "电商运营"
  if (scenarioTitle.includes("合规") || scenarioTitle.includes("法务")) return "合规法务"
  if (scenarioTitle.includes("炒股") || scenarioTitle.includes("投资")) return "量化投资"
  return "AI 自动化"
}

// ── Text generation (recommendation voice) ──

export function generateShareText(
  type: AbilityType,
  selectedIds: string[],
  scenarioTitle: string,
  options: {
    skills?: ShareableSkill[]
    agents?: ShareableAgent[]
    tools?: ShareableTool[]
    workflowNodes?: WorkflowNode[]
  }
): { title: string; description: string } {
  const domain = scenarioDomain(scenarioTitle)
  const count = selectedIds.length

  switch (type) {
    case "skill": {
      const selected = (options.skills ?? []).filter((s) => selectedIds.includes(s.id))
      if (count === 0) {
        return {
          title: `${scenarioTitle} 推荐 Skills`,
          description: `在「${scenarioTitle}」场景中用到的 Skills 组合，实测好用，推荐给有同样需求的朋友。`,
        }
      }
      const names = selected.map((s) => s.name).join("、")
      const descs = selected.map((s, i) => `${i + 1}. **${s.name}**：${s.role}`).join("\n")
      return {
        title: `推荐 ${count} 个好用的${domain} Skills`,
        description: `在「${scenarioTitle}」场景中实测好用的 Skills 组合：\n\n${descs}\n\n推荐给同样需要${domain}能力的朋友，一键安装即可使用。`,
      }
    }

    case "tool": {
      const selected = (options.tools ?? []).filter((t) => selectedIds.includes(t.id))
      if (count === 0) {
        return {
          title: `${scenarioTitle} 工具集`,
          description: `「${scenarioTitle}」中用到的核心工具，覆盖全流程关键节点。`,
        }
      }
      const descs = selected.map((t, i) => `${i + 1}. **${t.label}**（${t.agentName}）${t.result ? `→ ${t.result}` : ""}`).join("\n")
      return {
        title: `${scenarioTitle} 必备工具集`,
        description: `这些工具在「${scenarioTitle}」中表现出色：\n\n${descs}\n\n每个工具都经过实际验证，覆盖${domain}的关键环节。`,
      }
    }

    case "agent": {
      const selected = (options.agents ?? []).filter((a) => selectedIds.includes(a.id))
      if (count === 0) {
        return {
          title: `${scenarioTitle} Agent`,
          description: `在「${scenarioTitle}」中担任关键角色的 Agent，能力经过实际验证。`,
        }
      }
      if (count === 1) {
        const agent = selected[0]
        return {
          title: `${agent.name} — ${agent.role}专家`,
          description: `这个 Agent 在「${scenarioTitle}」中负责${agent.description}，内置 ${agent.skillNames.length} 个技能、${agent.toolCount} 个工具调用。\n\n擅长领域：${agent.role}\n使用技能：${agent.skillNames.join("、")}\n\n推荐给需要${domain}能力的朋友。`,
        }
      }
      const descs = selected.map((a) => `- **${a.name}**（${a.role}）：${a.description}`).join("\n")
      return {
        title: `推荐 ${count} 个${domain} Agent`,
        description: `这些 Agent 在「${scenarioTitle}」中各司其职：\n\n${descs}\n\n可以单独使用，也可以组合成工作流。`,
      }
    }

    case "workflow": {
      const nodes = options.workflowNodes ?? []
      const nodeNames = nodes.map((n) => n.name)
      const first = nodeNames[0] ?? "开始"
      const last = nodeNames[nodeNames.length - 1] ?? "完成"
      const flow = nodeNames.join(" → ")
      return {
        title: `${scenarioTitle} 全自动工作流`,
        description: `从「${first}」到「${last}」，${nodes.length} 个 Agent 节点协作完成整个${domain}流程。\n\n流程：${flow}\n\n一键复制即可使用，每个节点都可以根据需求自定义。`,
      }
    }

    case "agent-team": {
      const agents = options.agents ?? []
      const roles = agents.map((a) => `${a.name}(${a.role})`).join("、")
      return {
        title: `${scenarioTitle} 多 Agent 协作团队`,
        description: `${agents.length} 位 Agent 各司其职：${roles}。\n\n这支团队在「${scenarioTitle}」场景中完成了从头到尾的全流程自动化，推荐给有同样需求的朋友。一键复制整个团队配置。`,
      }
    }

    default:
      return {
        title: scenarioTitle,
        description: `基于 SafeClaw 多智能体平台的${domain}自动化方案。`,
      }
  }
}
